import React, { useState } from 'react';
import { Card } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Badge } from '../../../components/ui/Badge';
import { Input } from '../../../components/ui/Input';
import { ConfirmDialog } from '../../../components/ui/ConfirmDialog';
import { useToast } from '../../../hooks/useToast';
import { AdminPlan, formatKobo, plansApi } from '../../../api/plans';

const statusVariant = {
  published: 'success',
  draft: 'warning',
  retired: 'neutral',
} as const;

/**
 * A price as typed, in naira, to kobo. Null when it is not a price.
 *
 * Empty is not zero. `Number('')` is 0, so clearing the box and pressing
 * Save used to make the plan free.
 */
export function parseNaira(raw: string): number | null {
  const cleaned = raw.replace(/[₦,\s]/g, '');
  if (cleaned === '' || !/^\d+(\.\d{1,2})?$/.test(cleaned)) return null;
  return Math.round(Number(cleaned) * 100);
}

type Pending =
  | { kind: 'delete'; plan: AdminPlan }
  | { kind: 'default'; plan: AdminPlan }
  | { kind: 'retire'; plan: AdminPlan };

/**
 * The tiers and what they cost.
 *
 * Prices are entered in naira and stored in kobo — nobody types kobo, and a
 * price typed in the wrong unit is a hundredfold error nobody notices until
 * the first invoice.
 *
 * Every action reports what happened. The confirm dialogs used to call the
 * API with nothing to catch a refusal, so a delete or a default change the
 * server said no to left the dialog sitting there, silent, and a second
 * click sent it again.
 */
export const PlanList: React.FC<{
  plans: AdminPlan[];
  canManage: boolean;
  onChanged: () => void;
}> = ({ plans, canManage, onChanged }) => {
  const toast = useToast();
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState({ name: '', tagline: '', monthly: '', annual: '' });
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [pending, setPending] = useState<Pending | null>(null);

  const startEdit = (plan: AdminPlan) => {
    setEditing(plan.id);
    setFieldError(null);
    setDraft({
      name: plan.name,
      tagline: plan.tagline ?? '',
      monthly: String(plan.monthlyPriceKobo / 100),
      annual: String(plan.annualPriceKobo / 100),
    });
  };

  async function run(planId: string, action: () => Promise<unknown>, done: string, fallback: string) {
    setBusy(planId);
    try {
      await action();
      toast.success(done);
      onChanged();
      return true;
    } catch (err) {
      toast.error(err, fallback);
      return false;
    } finally {
      setBusy(null);
    }
  }

  const save = async (plan: AdminPlan) => {
    const monthly = parseNaira(draft.monthly);
    const annual = parseNaira(draft.annual);
    if (!draft.name.trim()) return setFieldError('A plan needs a name.');
    if (monthly === null) return setFieldError('Enter the monthly price in naira, for example 4500.');
    if (annual === null) return setFieldError('Enter the yearly price in naira, for example 45000.');
    setFieldError(null);
    const ok = await run(
      plan.id,
      () =>
        plansApi.updatePlan(plan.id, {
          name: draft.name.trim(),
          tagline: draft.tagline.trim(),
          monthlyPriceKobo: monthly,
          annualPriceKobo: annual,
        }),
      `${draft.name.trim()} saved.`,
      'Could not save that plan.',
    );
    if (ok) setEditing(null);
  };

  const confirm = async () => {
    if (!pending) return;
    const { plan } = pending;
    const ok =
      pending.kind === 'delete'
        ? await run(plan.id, () => plansApi.deletePlan(plan.id), `${plan.name} deleted.`, 'Could not delete that plan.')
        : pending.kind === 'default'
          ? await run(
              plan.id,
              () => plansApi.updatePlan(plan.id, { isDefault: true }),
              `${plan.name} is now the default.`,
              'Could not make that plan the default.',
            )
          : await run(
              plan.id,
              () => plansApi.updatePlan(plan.id, { status: 'retired' }),
              `${plan.name} retired.`,
              'Could not retire that plan.',
            );
    if (ok) setPending(null);
  };

  const dialog = pending
    ? pending.kind === 'delete'
      ? {
          title: 'Delete this plan?',
          message: `Nobody has ever been on ${pending.plan.name}, so deleting it is safe. It cannot be undone.`,
          label: 'Delete',
          danger: true,
        }
      : pending.kind === 'default'
        ? {
            title: 'Make this the default plan?',
            message: `Every business without a subscription will fall back to ${pending.plan.name}. Only one plan can be the default, so the current one stops being it.`,
            label: 'Make default',
            danger: false,
          }
        : {
            title: 'Retire this plan?',
            message: `${pending.plan.name} keeps working for anyone already on it, and nobody else can choose it. You can publish it again later.`,
            label: 'Retire',
            danger: true,
          }
    : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
          gap: 14,
        }}
      >
        {plans.map((plan) => {
          const current = plan.currentSubscribers ?? plan.subscribers;
          const past = plan.subscribers - current;
          const isBusy = busy === plan.id;
          return (
            <Card key={plan.id}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                  <div style={{ fontSize: 16, fontWeight: 600 }}>{plan.name}</div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    {plan.isDefault && <Badge variant="info">Default</Badge>}
                    <Badge variant={statusVariant[plan.status]}>{plan.status}</Badge>
                  </div>
                </div>

                <div style={{ fontSize: 12, color: 'var(--neutral-600)' }}>{plan.key}</div>

                {editing === plan.id ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <Input
                      label="Name"
                      value={draft.name}
                      onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
                    />
                    <Input
                      label="Tagline"
                      value={draft.tagline}
                      maxLength={200}
                      onChange={(e) => setDraft((d) => ({ ...d, tagline: e.target.value }))}
                    />
                    <Input
                      label="Monthly (₦)"
                      value={draft.monthly}
                      inputMode="decimal"
                      onChange={(e) => setDraft((d) => ({ ...d, monthly: e.target.value }))}
                    />
                    <Input
                      label="Yearly (₦)"
                      value={draft.annual}
                      inputMode="decimal"
                      hint="Two months free is the house discount: 10 times the monthly."
                      onChange={(e) => setDraft((d) => ({ ...d, annual: e.target.value }))}
                    />
                    {fieldError && <div style={{ color: 'var(--danger-900)', fontSize: 13 }}>{fieldError}</div>}
                    <div style={{ display: 'flex', gap: 8 }}>
                      <Button size="sm" onClick={() => void save(plan)} loading={isBusy} disabled={isBusy}>
                        Save
                      </Button>
                      <Button size="sm" variant="ghost" disabled={isBusy} onClick={() => setEditing(null)}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <>
                    {plan.tagline && (
                      <div style={{ fontSize: 13, color: 'var(--neutral-700)' }}>{plan.tagline}</div>
                    )}
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                      <span style={{ fontSize: 22, fontWeight: 600 }}>{formatKobo(plan.monthlyPriceKobo)}</span>
                      <span style={{ fontSize: 12, color: 'var(--neutral-600)' }}>/ month</span>
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--neutral-600)' }}>
                      {formatKobo(plan.annualPriceKobo)} / year
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--neutral-700)' }}>
                      {current} {current === 1 ? 'business' : 'businesses'} on this plan
                      {past > 0 ? ` · ${past} before` : ''}
                    </div>

                    {canManage && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        <Button size="sm" variant="secondary" disabled={isBusy} onClick={() => startEdit(plan)}>
                          Edit
                        </Button>
                        {plan.status !== 'published' && (
                          <Button
                            size="sm"
                            variant="secondary"
                            disabled={isBusy}
                            onClick={() =>
                              void run(
                                plan.id,
                                () => plansApi.updatePlan(plan.id, { status: 'published' }),
                                `${plan.name} published.`,
                                'Could not publish that plan.',
                              )
                            }
                          >
                            Publish
                          </Button>
                        )}
                        {plan.status === 'published' && !plan.isDefault && (
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={isBusy}
                            onClick={() => setPending({ kind: 'retire', plan })}
                          >
                            Retire
                          </Button>
                        )}
                        {/* Only a published plan can be the default: anything else
                            would put unsubscribed businesses on a plan the app
                            does not show. */}
                        {plan.status === 'published' && !plan.isDefault && (
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={isBusy}
                            onClick={() => setPending({ kind: 'default', plan })}
                          >
                            Make default
                          </Button>
                        )}
                        {!plan.isDefault && plan.subscribers === 0 && (
                          <Button
                            size="sm"
                            variant="danger"
                            disabled={isBusy}
                            onClick={() => setPending({ kind: 'delete', plan })}
                          >
                            Delete
                          </Button>
                        )}
                      </div>
                    )}
                    {canManage && !plan.isDefault && plan.subscribers > 0 && plan.status !== 'retired' && (
                      <div style={{ fontSize: 12, color: 'var(--neutral-500)' }}>
                        Businesses have been on this plan, so it can be retired but not deleted.
                      </div>
                    )}
                  </>
                )}
              </div>
            </Card>
          );
        })}
      </div>

      <ConfirmDialog
        isOpen={!!dialog}
        title={dialog?.title ?? ''}
        message={dialog?.message ?? ''}
        confirmLabel={dialog?.label}
        variant={dialog?.danger ? 'danger' : 'primary'}
        loading={!!pending && busy === pending.plan.id}
        onConfirm={() => void confirm()}
        onCancel={() => setPending(null)}
      />
    </div>
  );
};
